<template>
  <div class="lectures-calendar is-flex is-flex-direction-column">
    <!-- Date Navigation Controls -->
    <div class="is-flex is-justify-content-space-between is-align-items-center">
      <!-- View Selector Tabs -->
      <div>
        <b-tabs v-model="activeTabIndex" type="is-toggle" @input="onTabChange">
          <b-tab-item label="Month" value="month"></b-tab-item>
          <b-tab-item label="Week" value="week"></b-tab-item>
          <b-tab-item label="Day" value="day"></b-tab-item>
        </b-tabs>
      </div>

      <div class="is-flex is-align-items-center">
        <!-- Navigation Controls with View-Specific Date Pickers -->
        <div class="is-flex is-align-items-center">
          <!-- Previous Period with Lectures Button -->
          <b-button
            icon-left="angle-double-left"
            @click="navigateToPeriod(prevPeriodWithLecturesKey)"
            :disabled="!prevPeriodWithLecturesKey"
          >
          </b-button>

          <!-- Previous Button -->
          <b-button icon-left="chevron-left" @click="showPrev"> </b-button>

          <!-- View-Specific Date Picker -->
          <div>
            <!-- Month View: Month/Year Picker -->
            <b-datepicker
              v-if="selectedView === 'month'"
              v-model="selectedDate"
              @input="changeDate"
              :mobile-native="false"
              type="month"
              placeholder="Select month"
              icon="calendar"
            >
            </b-datepicker>

            <!-- Week/Day View: Regular datepicker with custom trigger -->
            <b-datepicker
              v-else
              v-model="selectedDate"
              @input="changeDate"
              :events="datePickerEvents"
              :placeholder="
                selectedView === 'week' ? 'Select week' : 'Select day'
              "
              icon="calendar"
            >
            </b-datepicker>
          </div>

          <!-- Next Button -->
          <b-button icon-left="chevron-right" @click="showNext"> </b-button>

          <!-- Next Period with Lectures Button -->
          <b-button
            icon-left="angle-double-right"
            @click="navigateToPeriod(nextPeriodWithLecturesKey)"
            :disabled="!nextPeriodWithLecturesKey"
          >
          </b-button>

          <!-- Today Button -->
          <b-button class="ml-2" rounded @click="changeDate(new Date())">
            Today
          </b-button>
        </div>
      </div>

      <!-- Filters -->
      <div class="calendar-header-right">
        <div class="calendar-filter has-text-left">
          <b-dropdown
            v-model="selectedClasses"
            multiple
            aria-role="list"
            position="is-bottom-left"
          >
            <template #trigger>
              <b-button type="is-default" icon-right="caret-down">
                {{ selectedClasses.length }}/{{ classList.length }} classes
              </b-button>
            </template>
            <b-dropdown-item
              aria-role="menu-item"
              :focusable="false"
              class="filter-buttons"
              custom
              paddingless
            >
              <b-button
                size="is-small"
                icon-left="check"
                type="is-success"
                outlined
                @click="selectedClasses = classList"
              >
                Select all
              </b-button>
              <b-button
                size="is-small"
                icon-left="times"
                type="is-danger"
                outlined
                @click="selectedClasses = []"
              >
                Clear all
              </b-button>
            </b-dropdown-item>
            <b-dropdown-item separator />

            <b-dropdown-item
              v-for="item in classList"
              :key="item.classId"
              :value="item"
              aria-role="listitem"
            >
              {{ formatClass(item) }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            v-model="selectedTeachers"
            multiple
            aria-role="list"
            position="is-bottom-left"
          >
            <template #trigger>
              <b-button type="is-default" icon-right="caret-down">
                {{ selectedTeachers.length }}/{{ teacherList.length }} coaches
              </b-button>
            </template>
            <b-dropdown-item
              aria-role="menu-item"
              :focusable="false"
              class="filter-buttons"
              custom
              paddingless
              v-if="selectedTeachers.length"
            >
              <b-button
                size="is-small"
                icon-left="check"
                type="is-success"
                outlined
                @click="selectedTeachers = teacherList"
              >
                Select all
              </b-button>
              <b-button
                size="is-small"
                icon-left="times"
                type="is-danger"
                outlined
                @click="selectedTeachers = []"
              >
                Clear all
              </b-button>
            </b-dropdown-item>
            <b-dropdown-item v-if="selectedTeachers.length" separator />

            <b-dropdown-item
              v-for="item in teacherList"
              :key="item.id"
              :value="item"
              aria-role="listitem"
            >
              {{ item.firstName }} {{ item.lastName }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>

    <div class="calendar-body columns is-desktop">
      <div class="calendar-view column">
        <VueCal
          ref="vuecal"
          class="vuecal--green-theme"
          hide-view-selector
          :disable-views="['years', 'year']"
          :time-from="7 * 60"
          :active-view="selectedView"
          :events="events"
          events-on-month-view="true"
          @cell-click="onCellClick"
          @event-mouse-enter="onEventClick"
          @event-mouse-leave="cancelEventClick"
        >
        </VueCal>
      </div>
    </div>

    <!-- Lecture Details Modal -->
    <b-modal
      v-model="isLectureModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{
              selectedLecture
                ? getClassnameById(selectedLecture.classroomId)
                : ""
            }}
          </p>
          <button
            type="button"
            class="delete"
            @click="isLectureModalActive = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <div v-if="selectedLecture">
            <!-- Lecture Information -->
            <div class="lecture-info mb-4">
              <div class="lecture-meta">
                <p>
                  <strong>Date:</strong>
                  {{ selectedLecture.lectureDate | asDate }}
                </p>
                <p>
                  <strong>Time:</strong>
                  {{ selectedLecture.lectureDate | asTime }}
                  <span v-if="selectedLecture.lectureEndDate">
                    - {{ selectedLecture.lectureEndDate | asTime }}
                  </span>
                </p>
                <p>
                  <strong>Coach:</strong> {{ selectedLecture.firstName }}
                  {{ selectedLecture.lastName }}
                </p>
                <h5 class="subtitle is-6">{{ selectedLecture.title }}</h5>
              </div>
            </div>

            <!-- Topics Section -->
            <div class="topics-section">
              <h5 class="title is-6 mb-3">Topics</h5>
              <div
                v-if="
                  selectedLecture.topicIds &&
                  selectedLecture.topicIds.length > 0
                "
                class="topics-list"
              >
                <CompactTopicCard
                  v-for="topicId in selectedLecture.topicIds"
                  :key="topicId"
                  :topic="lessonObj(topicId)"
                  :usage="' '"
                  :canRemove="false"
                  class="mb-2"
                />
              </div>
              <div v-else class="has-text-grey-light has-text-centered py-4">
                No topics assigned to this lecture
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="isLectureModalActive = false">Close</b-button>

          <b-button type="is-info" @click="goToLectures">
            Edit Lectures
          </b-button>

          <b-button
            v-if="selectedLecture"
            type="is-info"
            :disabled="!selectedLecture.topicIds.length"
            @click="goToLecture(selectedLecture.id)"
          >
            View Lecture
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { LectureService, TopicsService } from "@/services/CoachingServices";
import VueCal from "vue-cal";
import "vue-cal/dist/vuecal.css";
import { toDayjs } from "vue-chess-shared/utils/date-helper";
import { mapGetters } from "vuex";
import CompactTopicCard from "../explorer/molecules/CompactTopicCard.vue";

export default {
  name: "LecturesCalendar",

  components: { VueCal, CompactTopicCard },

  data() {
    return {
      activeTabIndex: 0, // 0: month, 1: week, 2: day
      selectedClasses: [],
      selectedTeachers: [],
      selectedCurriculum: [],
      selectedDate: new Date(),
      startDateOfCurrentView: null,
      lectures: [],
      curriculums: [],
      filteredLectures: [],
      isLectureModalActive: false,
      selectedLecture: null,
    };
  },

  watch: {
    selectedDate() {
      this.$refs.vuecal.updateSelectedDate(this.selectedDate);
    },

    selectedClasses() {
      this.filterLectures();
    },

    selectedTeachers() {
      this.filterLectures();
    },

    classList() {
      this.selectedClasses = this.classList;
    },
  },

  computed: {
    ...mapGetters({
      classList: "getClassList",
      currentTeacherId: "getTeacherId",
    }),

    events() {
      return this.filteredLectures.map((lecture) => {
        const now = new Date();
        const lectureDate = new Date(lecture.lectureDate);
        const isPast = lectureDate < now;
        const hasTopics = lecture.topicIds && lecture.topicIds.length > 0;
        const isMyLecture =
          this.currentTeacherId && lecture.teacherId === this.currentTeacherId;

        // Determine CSS class based on status
        let cssClass = `lecture-${isPast ? "past" : "future"}-${
          hasTopics ? "with-topics" : "no-topics"
        } ${isMyLecture ? "lecture-my-lecture" : ""}`;

        // assign 1 background color per class index
        const classIndex = this.classList.findIndex(
          (c) => c.classId === lecture.classroomId
        );
        const classColor = `class-${classIndex % 5}`;
        cssClass += ` ${classColor}`;

        return {
          start: lecture.lectureDate,
          end: lecture.lectureEndDate,
          title: this.getClassnameById(lecture.classroomId), // classname
          content: `<strong>${lecture.firstName} ${lecture.lastName}</strong> - #topics: ${lecture.topicIds.length}`,
          class: cssClass,
          lectureData: lecture, // Store lecture data for hover functionality
        };
      });
    },

    // Events formatted for the datepicker component (day view)
    datePickerEvents() {
      return this.filteredLectures.map((lecture) => {
        return lecture.lectureDate;
      });
    },

    teacherList() {
      return this.lectures.reduce((prev, curr) => {
        if (prev.find((c) => c.id === curr.teacherId)) {
          return prev;
        }

        prev.push({
          id: curr.teacherId,
          firstName: curr.firstName,
          lastName: curr.lastName,
        });

        return prev;
      }, []);
    },

    selectedView() {
      const views = ["month", "week", "day"];
      return views[this.activeTabIndex];
    },

    periodsWithLecturesMap() {
      const periods = new Map();
      this.filteredLectures.forEach((lecture) => {
        const periodKey = this.periodKey(lecture.lectureDate);
        periods.set(periodKey, [...(periods.get(periodKey) ?? []), lecture]);
      });

      return periods;
    },

    // Get unique periods (months/weeks/days) that have lectures
    periodsWithLectures() {
      return Array.from(this.periodsWithLecturesMap.keys()).sort();
    },

    currentPeriodKey() {
      return this.periodKey(this.selectedDate);
    },

    prevPeriodWithLecturesKey() {
      return this.periodsWithLectures.findLast(
        (p) => p < this.currentPeriodKey
      );
    },

    nextPeriodWithLecturesKey() {
      return this.periodsWithLectures.find((p) => p > this.currentPeriodKey);
    },
  },

  methods: {
    filterLectures() {
      const selectedClassIds = this.selectedClasses.map((c) => c.classId);
      const selectedTeacherIds = this.selectedTeachers.map((t) => t.id);

      this.filteredLectures = this.lectures.filter((l) =>
        selectedClassIds.includes(l.classroomId)
      );

      this.filteredLectures = this.filteredLectures.filter((l) =>
        selectedTeacherIds.includes(l.teacherId)
      );
    },

    getTopicById(topicId) {
      return this.topics.find((t) => t.id === topicId);
    },

    lessonObj(topicId) {
      return {
        ...this.getTopicById(topicId),
        isOptional: this.curriculum?.lessons?.find(
          (lesson) => lesson.topicId === topicId
        )?.isOptional,
        usage: " ",
      };
    },

    periodKey(date) {
      if (!date) return "";
      const dateObj = toDayjs(date);
      if (this.selectedView === "month") {
        return dateObj.format("YYYY-MM");
      } else if (this.selectedView === "week") {
        return `${dateObj.year()}-W${dateObj.week()}`;
      } else {
        return dateObj.format("YYYY-MM-DD");
      }
    },

    onCellClick(event) {
      this.selectedDate = event;
    },

    onEventClick(event) {
      if (!event.lectureData) return;

      this.delayTimer = setTimeout(() => {
        this.isLectureModalActive = true;
        this.selectedLecture = event.lectureData;
      }, 1000);
    },

    cancelEventClick() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer);
        this.delayTimer = null;
      }
    },

    goToLectures() {
      const isTeacherContext = this.$route.meta?.teacher ?? false;
      const lecturesPath = isTeacherContext ? "/teacher/lectures" : "/lectures";
      this.$router.push(lecturesPath);
      // Close the modal after navigation
      this.isLectureModalActive = false;
    },

    goToLecture(lectureId) {
      // Check if we're in teacher context
      const isTeacherContext = this.$route.meta?.teacher ?? false;
      const lecturePath = isTeacherContext
        ? `/teacher/lectures/${lectureId}`
        : `/lectures/${lectureId}`;
      this.$router.push(lecturePath);
      // Close the modal after navigation
      this.isLectureModalActive = false;
    },

    getClassnameById(classId) {
      const classData = this.classList.find((c) => c.classId === classId);
      return classData ? this.formatClass(classData) : classId;
    },

    formatClass(classData) {
      // Handle both currentClass format and classList item format
      return `${classData.schoolName}${
        classData.groupName ? ` | ${classData.groupName}` : ""
      }`;
    },

    refreshStartDateOfCurrentView() {
      this.$nextTick(() => {
        this.startDateOfCurrentView = toDayjs(
          this.$refs.vuecal?.view?.startDate ?? new Date()
        );
      });
    },

    showPrev() {
      this.$refs.vuecal.previous();
      this.refreshStartDateOfCurrentView();
      // Sync selectedDate with the new view
      this.$nextTick(() => {
        this.selectedDate = this.startDateOfCurrentView?.toDate() || new Date();
      });
    },

    showNext() {
      this.$refs.vuecal.next();
      this.refreshStartDateOfCurrentView();
      // Sync selectedDate with the new view
      this.$nextTick(() => {
        this.selectedDate = this.startDateOfCurrentView?.toDate() || new Date();
      });
    },

    navigateToPeriod(periodKey) {
      const targetDate =
        this.periodsWithLecturesMap.get(periodKey)[0].lectureDate;
      this.changeDate(targetDate);
    },

    onTabChange(tabIndex) {
      // Map tab index to view name
      const views = ["month", "week", "day"];
      const newView = views[tabIndex];

      if (newView && newView !== this.selectedView) {
        this.selectedView = newView;
        this.$refs.vuecal.switchView(newView);
        this.refreshStartDateOfCurrentView();
      }
    },

    changeView(value) {
      this.selectedView = value;
      // Update tab index to match the view
      const views = ["month", "week", "day"];
      this.activeTabIndex = views.indexOf(value);
      this.$refs.vuecal.switchView(value);
      this.refreshStartDateOfCurrentView();
    },

    changeDate(value) {
      this.selectedDate = value;
      this.$refs.vuecal.updateSelectedDate(value);
      this.refreshStartDateOfCurrentView();
    },

    changeTeacher() {},

    changeClass() {},
  },

  mounted() {
    this.refreshStartDateOfCurrentView();
  },

  async created() {
    this.lectures = await LectureService.getAll();
    this.topics = await TopicsService.getPublicAndUserTopics(
      this.currentTeacherId
    );
    this.selectedTeachers = this.teacherList;
    this.selectedClasses = this.classList;
  },
};
</script>

<style>
/* Global styles for lecture events */
.vuecal__event {
  background-color: #f5f5f5 !important;
  color: #363636 !important;
}

.vuecal__event.lecture-past-no-topics {
  border: 2px solid #d0cfcf !important;
  opacity: 0.5;
}

.vuecal__event.lecture-past-with-topics {
  border: 2px solid #587b91 !important;
  opacity: 0.7;
}

.vuecal__event.lecture-future-no-topics {
  border: 2px solid #ffeb3b !important;
}

.vuecal__event.lecture-future-with-topics {
  border: 2px solid #4caf50 !important;
}

/* Style for my lectures, make text color different */
.vuecal__event.lecture-my-lecture {
  color: #4904c8 !important;
}

/* Class-specific styles, use more vibrant colors*/
.vuecal__event.class-0 {
  background-color: #f8e9e9 !important;
}

.vuecal__event.class-1 {
  background-color: #e3f2fd !important;
}

.vuecal__event.class-2 {
  background-color: #fff3e0 !important;
}

.vuecal__event.class-3 {
  background-color: #e8f5e9 !important;
}

.vuecal__event.class-4 {
  background-color: #f3e5f5 !important;
}
</style>

<style scoped>
.date-navigation {
  gap: 0.5rem;
}

.date-picker-container {
  min-width: 200px;
  display: flex;
  align-items: center;
}

.calendar-body {
  margin-top: 0.5rem;
}

.calendar-view {
  height: 800px;
}

.calendar-filter {
  position: relative;
}

.calendar-filter .filter-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
